import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import LoadingSpinner from "@/app/components/LoadingSpinner"
import { usePdfBuilderStore, selectCurrentPageConfig } from "@/stores/pdfBuilder"
import { PAPER_DIMENSIONS } from "@/types/pdfBuilder"
import type { ChartOptions } from "@/types/pdfBuilder"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import {
  useActivityIntervalSummaryQuery,
  useSessionIntervalSummaryQuery,
  useSessionPlayerSummaryQuery,
} from "@/hooks/api/useReportQuery"
import { ACTIVITY_SESSION_TYPE, CHART_TYPE } from "@/app/report/common/constants"
import type { SessionDetailsSettings } from "@/app/report/common/type"
import {
  buildSessionDetailsData,
  getTemplateSessionDetailsCharts,
  resolveTemplateSessionDetailsChart,
} from "@/app/report/common/sections/sessionDetails"
import { Interval } from "@/types/report"
import { colors } from "@/components/colors"
import { createGroupId } from "@/app/report/pdf/builder/utils/paletteItems"
import {
  countEngineLines,
  dataLabelExtraPx,
  dataLabelToValuePosition,
  drawEngineChart,
  LEGEND_FALLBACK_PX,
  type EngineChartGroup,
} from "./engine/drawEngineChart"
import {
  planPageBlocks,
  GRID_ROW_PX,
  CANVAS_TOP_PX,
  fillToPageBottomRows,
  groupedTopPadPx,
} from "./engine/pageBlocks"
import { MAX_CHART_SERIES } from "./BuilderD3Chart"
import type { ChartComponentProps } from "./types"

import "./builderCharts.scss"

export type SessionDetailsView = "session" | "1m" | "5m" | "15m"

/** 컨테이너 안에 쌓이는 순서 */
export const SESSION_DETAILS_VIEW_ORDER: SessionDetailsView[] = ["session", "1m", "5m", "15m"]
export const SESSION_DETAILS_VIEW_LABELS: Record<
  SessionDetailsView,
  { key: string; label: string }
> = {
  session: { key: "session", label: "Session" },
  "1m": { key: "interval1m", label: "1 min" },
  "5m": { key: "interval5m", label: "5 min" },
  "15m": { key: "interval15m", label: "15 min" },
}

const DEFAULT_COLORS = [
  colors.blue["800"],
  colors.green["700"],
  colors.orange["700"],
  colors.pink["800"],
  colors.purple["900"],
  colors.primary["600"],
]

// 템플릿에 Session Details 섹션이 없을 때의 기본 — 설정 화면(daily/settings SessionDetailsSection) 기본값과 같다
const DEFAULT_SETTINGS: SessionDetailsSettings = {
  calcType: "average",
  yAxis: [
    {
      metric: "totalDistance",
      chartType: "bar",
      color: colors.blue[700],
      manualScaleActive: false,
      manualScaleValue: null,
    },
  ],
  dataLabel: "hideValues",
  decimalPlace: 1,
}

export { MAX_CHART_SERIES }

/** 차트 한 줄의 고정 높이(px) — 페이지 인식 배치는 줄 높이가 정해져 있어야 한다 */
const LINE_PX = 160
/** 보기 소제목 높이(px, builderCharts.scss .BuilderSessionDetails__subtitle) */
const SUBTITLE_PX = 24
/** 데이터 없음·불러오는 중 안내 높이(px, .BuilderSessionDetails__view-empty) */
const EMPTY_VIEW_PX = 40
// CanvasItem 크롬: 테두리 4 + 안쪽 여백 16 + 머리말 ≈ 30
const ITEM_CHROME_PX = 50
/** Manual 로 나눌 때 새로 만드는 아이템의 시작 높이·최소 높이(칸) */
const NEW_MEMBER_ROWS = 6
const MIN_ROWS = 4

/** 옛 단일 보기 아이템의 보기 — 값이 없거나 이상하면 세션 */
export const resolveSessionDetailsView = (view: string | undefined): SessionDetailsView =>
  view === "1m" || view === "5m" || view === "15m" ? view : "session"

/** 컨테이너(보기 목록을 옵션으로 켜고 끄는 새 방식)인지 — 옛 아이템은 intervalView 하나만 가진다 */
export const isSessionDetailsContainer = (options: ChartOptions) =>
  Array.isArray(options.intervalViews)

/** 아이템이 보여 줄 보기들 — 컨테이너는 옵션 순서와 무관하게 세션→1분→5분→15분, 옛 아이템은 하나 */
export const resolveSessionDetailsViews = (options: ChartOptions): SessionDetailsView[] => {
  if (Array.isArray(options.intervalViews)) {
    const enabled = options.intervalViews

    return SESSION_DETAILS_VIEW_ORDER.filter((view) => enabled.includes(view))
  }

  return [resolveSessionDetailsView(options.intervalView)]
}

type ViewBlock = {
  view: SessionDetailsView
  groups: EngineChartGroup[]
  loading: boolean
  needsSessionMode: boolean
}

/**
 * PDF 빌더 Session Details — 리포트 화면의 Session Details 섹션과 같은 것 (일감 #75, 5단계-b).
 *
 * 2026-09-30 개편: 한 아이템이 컨테이너다. 세션(세션별 요약)·1분·5분·15분(인터벌 요약) 보기를 옵션(intervalViews)으로
 * 켜고 끄며, 켜진 보기가 소제목과 함께 위에서 아래로 쌓인다. 처음 추가하면 세션·5분이 켜져 있다.
 * - 줄(차트 하나) 높이는 LINE_PX 로 고정하고, 페이지 경계에 걸리는 줄은 앞에 여백을 두어 다음 페이지로 민다
 *   (planPageBlocks — Table·세로 차트와 같은 규칙). 칸 높이는 내용을 따라간다.
 * - Layout(options.tableScale) 이 manual 이면 보기마다 아이템 하나(같은 groupId)로 나눈다 — 첫 아이템이 보기 수에 맞춰
 *   아이템을 늘리고 줄이며, 둘째부터는 제목·삭제 버튼이 없다(CanvasItem). 각 아이템 안에서도 줄 단위 페이지 여백은 같다.
 * - 옛 아이템(intervalView 하나, 넷이 한 묶음)은 그대로 단일 보기로 그린다.
 * 축 구성·값 추출·인터벌 행은 리포트와 같은 buildSessionDetailsData, 그리기는 같은 엔진(createChart)이다.
 * 지표는 처음 추가될 때 템플릿 차트의 지표·색을 가져와 아이템별로 가감한다 (Graph 와 같은 규칙).
 */
export default function BuilderSessionDetails({ item, width }: ChartComponentProps) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const {
    activityDetail,
    reportTemplate,
    reportMode,
    metricList,
    metricMap,
    unitList,
    resolveItemPlayerIds,
    hasActivitySelection,
    hasFilterSelection,
  } = usePdfDataContext()
  const filterActivityId = usePdfBuilderStore((s) => s.filterActivityId)
  const filterSessionIds = usePdfBuilderStore((s) => s.filterSessionIds)
  const currentPageId = usePdfBuilderStore((s) => s.currentPageId)
  const updateItem = usePdfBuilderStore((s) => s.updateItem)
  const updateItemLayout = usePdfBuilderStore((s) => s.updateItemLayout)
  const isExporting = usePdfBuilderStore((s) => s.isExporting)
  const setHeadGap = usePdfBuilderStore((s) => s.setHeadGap)
  const viewsRef = useRef<HTMLDivElement>(null)
  const chartRefs = useRef<Partial<Record<SessionDetailsView, HTMLDivElement | null>>>({})

  const pageConfig = usePdfBuilderStore(selectCurrentPageConfig)
  const pageDims = PAPER_DIMENSIONS[pageConfig?.size === "letter" ? "letter" : "a4"]
  const pageHeight = pageConfig?.orientation === "landscape" ? pageDims.width : pageDims.height

  const isSessionMode = reportMode === ACTIVITY_SESSION_TYPE.SESSION
  const isContainer = isSessionDetailsContainer(item.options)
  const intervalViewsKey = (item.options.intervalViews ?? []).join(",")
  const enabledViews = useMemo(
    () => resolveSessionDetailsViews(item.options),
    [isContainer, intervalViewsKey, item.options.intervalView],
  )
  const isManual = isContainer && (item.options.tableScale ?? "auto") === "manual"

  // ── Manual 묶음: 같은 groupId 아이템을 위→아래 순으로 세어 이 아이템이 몇 번째(rank)인지 정한다 (BuilderMap 과 같은 규칙)
  const groupId = item.options.groupId
  const groupKey = usePdfBuilderStore((s) => {
    if (!groupId || !isContainer) {
      return ""
    }
    const page = s.pages.find((p) => p.id === s.currentPageId)

    return (page?.items ?? [])
      .filter((candidate) => candidate.options.groupId === groupId)
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map((candidate) => candidate.id)
      .join(",")
  })
  const groupMemberIds = useMemo(() => (groupKey ? groupKey.split(",") : []), [groupKey])
  const isGrouped = groupMemberIds.length > 0
  const groupRank = isGrouped ? Math.max(0, groupMemberIds.indexOf(item.id)) : 0

  useEffect(() => {
    if (isManual && !groupId && currentPageId && !isExporting) {
      updateItem(currentPageId, item.id, {
        options: { ...item.options, groupId: createGroupId("sd") },
      })
    }
  }, [isManual, groupId, currentPageId, isExporting, updateItem, item.id, item.options])

  // 이 아이템이 그릴 보기 — Manual 이면 자기 순번의 보기 하나, 아니면 켜진 보기 전부
  const myViews = useMemo<SessionDetailsView[]>(() => {
    if (isManual && isGrouped) {
      const mine = enabledViews[groupRank]

      return mine ? [mine] : []
    }

    return enabledViews
  }, [isManual, isGrouped, enabledViews, groupRank])
  const neededMembers = isManual ? Math.max(1, enabledViews.length) : 1
  const isLastMember = !isManual || !isGrouped || groupRank >= neededMembers - 1
  // Manual 묶음(보기마다 아이템 하나)도 Table 과 같게 페이지 경계로 나눈다 — 자투리에서 시작하면 다음 페이지 첫머리로,
  // 마지막이 아닌 아이템은 페이지 끝까지 채워 다음 보기가 다음 페이지에서 시작한다 (2026-10-01 결정)
  const groupedTopPad =
    isManual && isGrouped ? groupedTopPadPx(item.y * GRID_ROW_PX + CANVAS_TOP_PX, pageHeight) : 0

  // 첫 아이템이 묶음의 아이템 수를 보기 수에 맞춘다 — 모자라면 마지막 아이템 바로 아래에 붙이고(그 자리 이후 아이템은 먼저
  // 밀어 둔다), 남으면 뒤에서부터 지운다. Auto 로 돌아오면 첫 아이템만 남긴다
  useEffect(() => {
    if (!isContainer || !isGrouped || groupRank !== 0 || isExporting || !currentPageId) {
      return
    }
    const state = usePdfBuilderStore.getState()
    const page = state.pages.find((p) => p.id === currentPageId)
    if (!page) {
      return
    }
    const members = page.items
      .filter((candidate) => candidate.options.groupId === groupId)
      .sort((a, b) => a.y - b.y || a.x - b.x)
    if (members.length < neededMembers) {
      const selectedBefore = state.selectedItemId
      const last = members[members.length - 1] ?? item
      const insertY = last.y + last.h
      const shift = (neededMembers - members.length) * NEW_MEMBER_ROWS
      page.items
        .filter((other) => other.options.groupId !== groupId && other.y >= insertY)
        .forEach((other) => state.updateItemLayout(currentPageId, other.id, { y: other.y + shift }))
      let nextY = insertY
      for (let index = members.length; index < neededMembers; index++) {
        state.addItem(currentPageId, {
          chartType: "sessionDetails",
          x: last.x,
          y: nextY,
          w: last.w,
          h: NEW_MEMBER_ROWS,
          minW: last.minW,
          minH: MIN_ROWS,
          dataSource: { ...last.dataSource },
          options: { ...last.options },
        })
        nextY += NEW_MEMBER_ROWS
      }
      state.selectItem(selectedBefore)
    } else if (members.length > neededMembers) {
      members.slice(neededMembers).forEach((extra) => state.removeItem(currentPageId, extra.id))
    }
  }, [isContainer, isGrouped, groupRank, groupId, isExporting, currentPageId, neededMembers, item])

  // ── 설정: 템플릿 Session Details 차트 + 아이템 지표(가감)·색 ──
  const templateCharts = useMemo(
    () => getTemplateSessionDetailsCharts(reportTemplate),
    [reportTemplate],
  )
  const templateChart = useMemo(
    () =>
      resolveTemplateSessionDetailsChart(
        templateCharts,
        item.dataSource.sectionIndex,
        item.dataSource.chartIndex,
      ),
    [templateCharts, item.dataSource.sectionIndex, item.dataSource.chartIndex],
  )
  const baseSettings = templateChart?.settings ?? DEFAULT_SETTINGS

  const itemMetrics = item.dataSource.metrics
  useEffect(() => {
    if (itemMetrics !== undefined || !currentPageId) {
      return
    }
    const seeded = baseSettings.yAxis.slice(0, MAX_CHART_SERIES)
    updateItem(currentPageId, item.id, {
      dataSource: { ...item.dataSource, metrics: seeded.map((y) => y.metric) },
      options: {
        ...item.options,
        colors: seeded.map((y) => y.color),
        // 소수점 자릿수도 만들 때 스냅샷 — 템플릿 0 은 옛 설정 화면이 "0.1"로 보여 주던 미지정 값이라 1(0.1)로 (2026-10-01)
        decimalPlaces: baseSettings.decimalPlace || 1,
        // 최대값(수동 스케일)은 만들 때 스냅샷 — 템플릿이 바뀌어도 저장된 값을 쓴다 (2026-10-01 결정)
        metricScales: Object.fromEntries(
          seeded.map((y) => [
            y.metric,
            y.manualScaleActive && y.manualScaleValue != null ? y.manualScaleValue : null,
          ]),
        ),
      },
    })
  }, [itemMetrics, baseSettings, currentPageId, item.id, item.dataSource, item.options, updateItem])

  // 지표 최대 3개, 지표마다 종류는 아이템 지정 → 템플릿 축 종류 → bar
  const metrics = useMemo(
    () => (itemMetrics ?? baseSettings.yAxis.map((y) => y.metric)).slice(0, MAX_CHART_SERIES),
    [itemMetrics, baseSettings],
  )
  const itemColors = item.options.colors
  const metricChartTypes = item.options.metricChartTypes
  const metricScales = item.options.metricScales
  const decimalPlaces = item.options.decimalPlaces
  const settings = useMemo<SessionDetailsSettings>(
    () => ({
      ...baseSettings,
      // 소수점 자릿수 — 아이템 옵션이 우선, 없으면 템플릿(0 은 미지정이라 0.1) (2026-10-01 요청)
      decimalPlace: decimalPlaces ?? (baseSettings.decimalPlace || 1),
      yAxis: metrics.map((metricKey, idx) => {
        const fromTemplate = baseSettings.yAxis.find((y) => y.metric === metricKey)
        const color =
          itemColors?.[idx] ?? fromTemplate?.color ?? DEFAULT_COLORS[idx % DEFAULT_COLORS.length]
        const chartType = metricChartTypes?.[metricKey] ?? fromTemplate?.chartType ?? CHART_TYPE.BAR
        // 최대값 — 아이템에 지표별 값이 있으면(null 은 자동) 템플릿보다 우선 (2026-10-01 결정)
        const scaleEntry = metricScales?.[metricKey]
        const scale =
          scaleEntry === undefined
            ? null
            : { manualScaleActive: scaleEntry != null, manualScaleValue: scaleEntry }

        return fromTemplate
          ? { ...fromTemplate, color, chartType, ...(scale ?? {}) }
          : {
              metric: metricKey,
              chartType,
              color,
              manualScaleActive: scale?.manualScaleActive ?? false,
              manualScaleValue: scale?.manualScaleValue ?? null,
            }
      }),
    }),
    [baseSettings, metrics, itemColors, metricChartTypes, metricScales, decimalPlaces],
  )

  // ── 데이터 범위: 선수는 Data Source, 인터벌 세션은 아이템 지정(없으면 첫 세션 — 리포트 기본과 같다) ──
  const playerIds = useMemo(
    () => resolveItemPlayerIds(item.dataSource.playerIds),
    [resolveItemPlayerIds, item.dataSource.playerIds],
  )
  // Data Source 의 세션 — 손대지 않았으면 기본(세션 보기는 필터 전체, 인터벌은 첫 세션), 전부 해제하면 빈 목록이라
  // 데이터가 나오지 않는다 (선수 선택과 같은 규칙, 2026-10-01 요청)
  const itemSessionIds = item.dataSource.sessionIds
  const sessionViewIds = useMemo(
    () =>
      itemSessionIds === undefined
        ? filterSessionIds
        : itemSessionIds.filter((id) => filterSessionIds.includes(id)),
    [itemSessionIds, filterSessionIds],
  )
  const intervalSessionIds = useMemo(
    () =>
      itemSessionIds === undefined
        ? filterSessionIds.slice(0, 1)
        : itemSessionIds.filter((id) => filterSessionIds.includes(id)),
    [itemSessionIds, filterSessionIds],
  )

  const hasPlayers = playerIds.length > 0
  const wants = (view: SessionDetailsView) => myViews.includes(view)
  const eventIds = useMemo(() => (filterActivityId ? [filterActivityId] : []), [filterActivityId])
  const activityIntervalOn = (view: SessionDetailsView) =>
    hasFilterSelection && !isSessionMode && wants(view) && !!filterActivityId && hasPlayers
  const sessionIntervalOn = (view: SessionDetailsView) =>
    hasFilterSelection &&
    isSessionMode &&
    wants(view) &&
    intervalSessionIds.length > 0 &&
    hasPlayers

  // 훅 수는 고정 — 보기마다 조회 하나씩, 켜진 것만 enabled
  const sessionQuery = useSessionPlayerSummaryQuery(
    { sessions: sessionViewIds, players: playerIds },
    hasFilterSelection &&
      isSessionMode &&
      wants("session") &&
      sessionViewIds.length > 0 &&
      hasPlayers,
  )
  const activity1m = useActivityIntervalSummaryQuery(
    { interval: "1m" as Interval, eventIds, players: playerIds },
    activityIntervalOn("1m"),
  )
  const activity5m = useActivityIntervalSummaryQuery(
    { interval: "5m" as Interval, eventIds, players: playerIds },
    activityIntervalOn("5m"),
  )
  const activity15m = useActivityIntervalSummaryQuery(
    { interval: "15m" as Interval, eventIds, players: playerIds },
    activityIntervalOn("15m"),
  )
  const session1m = useSessionIntervalSummaryQuery(
    { interval: "1m" as Interval, eventIds: intervalSessionIds, players: playerIds },
    sessionIntervalOn("1m"),
  )
  const session5m = useSessionIntervalSummaryQuery(
    { interval: "5m" as Interval, eventIds: intervalSessionIds, players: playerIds },
    sessionIntervalOn("5m"),
  )
  const session15m = useSessionIntervalSummaryQuery(
    { interval: "15m" as Interval, eventIds: intervalSessionIds, players: playerIds },
    sessionIntervalOn("15m"),
  )
  const intervalQueries = {
    "1m": { activity: activity1m, session: session1m },
    "5m": { activity: activity5m, session: session5m },
    "15m": { activity: activity15m, session: session15m },
  } as const

  // 보기마다 데이터 → 엔진에 넘길 묶음(groups). 세션 보기는 한 묶음, 인터벌은 세션(또는 액티비티)마다 한 묶음
  const viewBlocks = useMemo<ViewBlock[]>(() => {
    if (!hasFilterSelection || !metricList.length || !unitList.length) {
      return myViews.map((view) => ({
        view,
        groups: [],
        loading: false,
        needsSessionMode: view === "session" && !isSessionMode,
      }))
    }

    return myViews.map((view) => {
      const needsSessionMode = view === "session" && !isSessionMode
      const interval = view === "session" ? null : intervalQueries[view]
      const data = buildSessionDetailsData({
        settings,
        metricMap,
        metricList,
        selectedUnitList: unitList,
        activitySessionType: reportMode,
        selectedSessionIds: intervalSessionIds,
        sessionSelectedRoot: filterSessionIds,
        sessionsInfo: activityDetail?.sessions ?? [],
        sessionPlayerSummary: view === "session" ? (sessionQuery.data ?? null) : null,
        sessionIntervalSummary: interval?.session.data ?? null,
        activityIntervalSummary: interval?.activity.data ?? null,
        interval: view === "session" ? 5 : parseInt(view, 10),
        t,
        translateKey,
      })
      const rawGroups: EngineChartGroup[] = !data
        ? []
        : view === "session"
          ? data.dataSession.length
            ? [{ name: "", nickname: null, rows: data.dataSession }]
            : []
          : data.dataInterval
              .filter((chart) => chart.data.length > 0)
              .map((chart) => ({ name: chart.name, nickname: chart.nickname, rows: chart.data }))
      // 정렬 — 아이템 옵션의 지표 Low-High/High-Low. 기본(default)은 세션·시간 순 그대로 (2026-10-01 요청)
      const sortIndex =
        item.options.sortKey && item.options.sortKey !== "default"
          ? settings.yAxis.findIndex((y) => y.metric === item.options.sortKey)
          : -1
      const groups =
        sortIndex < 0
          ? rawGroups
          : rawGroups.map((group) => ({
              ...group,
              rows: [...group.rows].sort((a, b) => {
                const diff = (a.data[sortIndex] ?? 0) - (b.data[sortIndex] ?? 0)

                return item.options.sortOrder === "desc" ? -diff : diff
              }),
            }))
      const loading =
        view === "session"
          ? sessionQuery.isLoading
          : isSessionMode
            ? !!interval?.session.isLoading
            : !!interval?.activity.isLoading

      return { view, groups, loading, needsSessionMode }
    })
  }, [
    hasFilterSelection,
    myViews,
    settings,
    metricMap,
    metricList,
    unitList,
    reportMode,
    isSessionMode,
    intervalSessionIds,
    filterSessionIds,
    activityDetail,
    sessionQuery.data,
    sessionQuery.isLoading,
    activity1m.data,
    activity1m.isLoading,
    activity5m.data,
    activity5m.isLoading,
    activity15m.data,
    activity15m.isLoading,
    session1m.data,
    session1m.isLoading,
    session5m.data,
    session5m.isLoading,
    session15m.data,
    session15m.isLoading,
    item.options.sortKey,
    item.options.sortOrder,
    t,
    translateKey,
  ])

  const axisY = useMemo(() => {
    const first = viewBlocks.find((block) => block.groups.length)
    if (!first || !hasFilterSelection || !metricList.length || !unitList.length) {
      return []
    }

    return (
      buildSessionDetailsData({
        settings,
        metricMap,
        metricList,
        selectedUnitList: unitList,
        activitySessionType: reportMode,
        selectedSessionIds: intervalSessionIds,
        sessionSelectedRoot: filterSessionIds,
        sessionsInfo: activityDetail?.sessions ?? [],
        sessionPlayerSummary: null,
        sessionIntervalSummary: null,
        activityIntervalSummary: null,
        interval: 5,
        t,
        translateKey,
      })?.axisY ?? []
    )
  }, [
    viewBlocks,
    hasFilterSelection,
    settings,
    metricMap,
    metricList,
    unitList,
    reportMode,
    intervalSessionIds,
    filterSessionIds,
    activityDetail,
    t,
    translateKey,
  ])

  const showLegend = item.options.showLegend ?? true
  // 모아서 보기·그래프 위 값은 엔진이 차트 위 공간을 쓴다 — 그만큼 줄을 키워 막대 크기를 지킨다 (2026-10-01 요청)
  const linePx =
    LINE_PX + dataLabelExtraPx(item.options.dataLabel ?? settings.dataLabel, settings.yAxis.length)
  // 보기들이 칸 위에서 얼마나 내려와 시작하는지(머리말·범례 아래, 레이아웃 px) — 그린 뒤 잰다
  const [contentTopPx, setContentTopPx] = useState(0)

  // ── 페이지 배치: 보기마다 [소제목+첫 줄, 줄, 줄…] 블록으로 쌓고, 경계에 걸리는 블록은 다음 페이지로 민다 ──
  const plan = useMemo(() => {
    const blockHeights: number[] = []
    const perView: Array<{ lines: number; firstIndex: number }> = []
    viewBlocks.forEach((block) => {
      const lines = block.groups.length ? countEngineLines(block.groups, settings.yAxis) : 0
      const subtitle = isContainer ? SUBTITLE_PX : 0
      perView.push({ lines, firstIndex: blockHeights.length })
      if (lines === 0) {
        blockHeights.push(subtitle + EMPTY_VIEW_PX)

        return
      }
      blockHeights.push(subtitle + linePx)
      for (let line = 1; line < lines; line++) {
        blockHeights.push(linePx)
      }
    })
    const legendPx = showLegend ? LEGEND_FALLBACK_PX : 0
    // 머리말·범례(rawTop)를 첫 블록에 붙인다 — 제목만 페이지 끝에 남지 않게 (2026-09-30 요청). 첫 블록의 여백(headGap)은
    // CanvasItem 위에 들어가 제목까지 같이 다음 페이지로 내려간다
    const rawTop = contentTopPx > 0 ? contentTopPx : ITEM_CHROME_PX + legendPx
    if (blockHeights.length) {
      blockHeights[0] += rawTop
    }
    const { padTops, totalHeight } = planPageBlocks(
      blockHeights,
      item.y * GRID_ROW_PX + CANVAS_TOP_PX,
      pageHeight,
    )

    return {
      // 위 여백 + 머리말·범례 + 보기들 — 칸 높이는 이 값으로 정한다
      totalHeight,
      headGap: padTops[0] ?? 0,
      views: perView.map(({ lines, firstIndex }) => ({
        firstPad: firstIndex === 0 ? 0 : (padTops[firstIndex] ?? 0),
        // 둘째 줄부터의 여백 — 엔진에 줄 순서대로 넘긴다(첫 줄은 보기 상자가 여백을 가진다)
        linePads: [0, ...padTops.slice(firstIndex + 1, firstIndex + Math.max(1, lines))],
      })),
    }
  }, [viewBlocks, settings.yAxis, isContainer, showLegend, item.y, contentTopPx, pageHeight])
  const planKey = JSON.stringify(plan)

  // ── 그리기 ──
  useEffect(() => {
    viewBlocks.forEach((block, index) => {
      const el = chartRefs.current[block.view]
      if (!el) {
        return
      }
      if (!block.groups.length) {
        el.innerHTML = ""

        return
      }
      drawEngineChart({
        container: el,
        groups: block.groups,
        axisY,
        yAxisSettings: settings.yAxis,
        width,
        height: linePx,
        lineHeightPx: linePx,
        padTops: plan.views[index]?.linePads,
        valuePositionType: dataLabelToValuePosition(item.options.dataLabel ?? settings.dataLabel),
        decimalPoint: settings.decimalPlace ?? 1,
        translateKey,
        t,
      })
    })
  }, [viewBlocks, axisY, settings, width, planKey, translateKey, t, item.options.dataLabel, linePx])

  // 보기 영역의 칸 안 오프셋 — 배치가 끝난 뒤 한 번 잰다 (창이 그려지지 않는 상태에서는 rAF 가 멈추므로 타이머를 함께 건다)
  useEffect(() => {
    let done = false
    const run = () => {
      if (done) {
        return
      }
      done = true
      const wrapper = viewsRef.current
      const grid = wrapper?.closest<HTMLElement>(".react-grid-item")
      if (!wrapper || !grid || grid.offsetHeight <= 0) {
        return
      }
      const gridRect = grid.getBoundingClientRect()
      const zoom = gridRect.height / grid.offsetHeight || 1
      // CanvasItem 에 적용된 위 여백(headGap)은 뺀다 — 여백 없는 순수 오프셋(머리말·범례)만 둔다
      const appliedGap = usePdfBuilderStore.getState().headGaps[item.id] ?? 0
      const top = Math.round(
        (wrapper.getBoundingClientRect().top - gridRect.top) / zoom - appliedGap,
      )
      setContentTopPx((prev) => (Math.abs(prev - top) > 1 ? top : prev))
    }
    const frame = requestAnimationFrame(run)
    const timer = setTimeout(run, 100)

    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [viewBlocks, width, showLegend, item.options.title, item.id])

  // 첫 블록(머리말·범례 + 첫 줄)이 경계에 걸리면 그만큼 CanvasItem 위 여백으로
  useEffect(() => {
    // Manual 묶음은 자투리 여백, Auto 는 첫 블록의 페이지 여백
    setHeadGap(item.id, isManual && isGrouped ? groupedTopPad : plan.headGap)
  }, [plan.headGap, item.id, setHeadGap, isManual, isGrouped, groupedTopPad])

  // ── 높이: 내용(보기 소제목 + 줄 × 고정 높이 + 페이지 여백)이 하한 — 그 아래로는 못 줄이고, 위로 늘린 만큼은 빈 여백 ──
  const lastAutoRowsRef = useRef<number | null>(null)
  useEffect(() => {
    if (!currentPageId || isExporting || !myViews.length) {
      return
    }
    // totalHeight 에 위 여백·머리말·범례가 들어 있다. 칸 px = h×40−10 ≥ 내용 + 안쪽 여백 10
    let rows = Math.max(MIN_ROWS, Math.ceil((plan.totalHeight + 20) / GRID_ROW_PX))
    if (!isLastMember) {
      rows = Math.max(
        rows,
        fillToPageBottomRows(item.y * GRID_ROW_PX + CANVAS_TOP_PX, groupedTopPad, pageHeight),
      )
    }
    // 내용 높이가 하한. 사용자가 그보다 늘린 높이는 지킨다(아래가 빈 여백) — 내용을 따라가던 칸(직전 자동값과 같음)만
    // 내용이 바뀔 때 같이 움직인다 (2026-09-30 요청)
    const followContent = lastAutoRowsRef.current !== null && item.h === lastAutoRowsRef.current
    const nextH = followContent ? rows : Math.max(item.h, rows)
    lastAutoRowsRef.current = rows
    const patch: { minH?: number; h?: number } = {}
    if (item.minH !== rows) {
      patch.minH = rows
    }
    if (item.h !== nextH) {
      patch.h = nextH
    }
    if (Object.keys(patch).length) {
      updateItemLayout(currentPageId, item.id, patch)
    }
  }, [
    currentPageId,
    isExporting,
    myViews.length,
    showLegend,
    contentTopPx,
    plan.totalHeight,
    isLastMember,
    groupedTopPad,
    item.y,
    pageHeight,
    item.id,
    item.h,
    item.minH,
    updateItemLayout,
  ])

  const renderEmpty = (message: string, loading = false) => (
    <div
      className={`BuilderSessionDetails BuilderSessionDetails--empty${loading ? " loading" : ""}`}
      style={{ color: colors.neutral.grey["600"], fontSize: 13 }}>
      {loading ? <LoadingSpinner /> : message}
    </div>
  )

  if (!hasActivitySelection) {
    return renderEmpty(t("selectActivity", "Select an activity from Report Filters"))
  }
  if (!myViews.length) {
    return renderEmpty(t("selectSessionDetailsView", "Turn on a view in the options"))
  }
  // 옛 단일 보기 아이템의 안내는 예전과 같다
  if (!isContainer && viewBlocks[0]?.needsSessionMode) {
    return renderEmpty(
      t("sessionViewNeedsSessionMode", "The session chart needs Session mode in Report Filters"),
    )
  }
  if (!isContainer && viewBlocks[0]?.loading && !viewBlocks[0].groups.length) {
    return renderEmpty("", true)
  }
  if (!isContainer && !viewBlocks[0]?.groups.length) {
    return renderEmpty(t("noData", "No data"))
  }

  return (
    <div className="BuilderD3Chart BuilderSessionDetails">
      {showLegend && axisY.length > 0 && (
        <div className="BuilderD3Chart__legend">
          {axisY.map((y) => (
            <div key={y.key} className="BuilderD3Chart__legend-item" style={{ color: y.color }}>
              <span>●</span>
              {/* 범례에 단위를 붙인다 (2026-10-01 요청) */}
              <span>{`${translateKey(y.key)}${y.unit ? ` (${y.unit})` : ""}`}</span>
            </div>
          ))}
        </div>
      )}
      <div ref={viewsRef} className="BuilderSessionDetails__views">
        {viewBlocks.map((block, index) => {
          const label = SESSION_DETAILS_VIEW_LABELS[block.view]
          const emptyMessage = block.needsSessionMode
            ? t(
                "sessionViewNeedsSessionMode",
                "The session chart needs Session mode in Report Filters",
              )
            : block.loading
              ? ""
              : t("noData", "No data")

          return (
            <div
              key={block.view}
              className="BuilderSessionDetails__view"
              style={{ paddingTop: plan.views[index]?.firstPad ?? 0 }}>
              {isContainer && (
                <div className="BuilderSessionDetails__subtitle">{t(label.key, label.label)}</div>
              )}
              {!block.groups.length && (
                <div
                  className={`BuilderSessionDetails__view-empty${block.loading ? " loading" : ""}`}>
                  {block.loading ? <LoadingSpinner /> : emptyMessage}
                </div>
              )}
              <div
                ref={(el) => {
                  chartRefs.current[block.view] = el
                }}
                className="BuilderD3Chart__vertical"
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
